import { useFormContext, type RegisterOptions } from 'react-hook-form'
import { cn } from '#/lib/utils'
import { LuChevronDown } from 'react-icons/lu'

type Option = { value: string; label: string }

type FormSelectProps = {
  name: string
  label: string
  placeholder?: string
  options: Option[]
  rules?: RegisterOptions
  className?: string
  icon?: React.ReactNode
}

export function FormSelect({
  name,
  label,
  placeholder,
  options,
  rules,
  className,
  icon,
}: FormSelectProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext()

  const error = errors[name]?.message as string | undefined

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={name} className="text-sm font-medium text-neutral-700">
        {label}
        {rules?.required && <span className="ml-1 text-primary-500">*</span>}
      </label>

      <div
        className={cn(
          'relative flex items-center overflow-hidden rounded-xl border bg-white transition-all duration-200',
          'focus-within:border-primary-500 focus-within:ring-3 focus-within:ring-primary-500/15',
          error
            ? 'border-red-400 ring-2 ring-red-400/15'
            : 'border-neutral-200 hover:border-neutral-300',
        )}
      >
        {icon && (
          <span className="pointer-events-none absolute left-4 text-base text-neutral-400">
            {icon}
          </span>
        )}

        <select
          id={name}
          {...register(name, rules)}
          className={cn(
            'h-12 w-full appearance-none bg-transparent pr-10 text-sm text-neutral-900 outline-none',
            icon ? 'pl-11' : 'pl-4',
          )}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <LuChevronDown className="pointer-events-none absolute right-4 text-neutral-400" />
      </div>

      {error && (
        <p role="alert" className="text-xs font-medium text-red-500">
          {error}
        </p>
      )}
    </div>
  )
}
